import { Box, Stack, Stepper as MantineStepper } from '@mantine/core'
import { BidderCatalog } from '@pubstack/common/src/bidderCatalog'
import { BidderSeat, buildSparkForPubPlacementId, PBSTCK_BIDDER_SEAT_CODE, SPARK_FOR_PUB_MIN_DECLARED_PLACEMENTS } from '@pubstack/common/src/bidder-seat'
import { UPGRADE_OFFER_FEATURE } from '@pubstack/common/src/upgrade'
import { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useBidderSeats } from '~/api/bidder-seats.api'
import { useCatalog, useMarketplaceBiddders } from '~/api/catalog.api'
import { Sizes } from '~/assets/style/tokens'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { Flyout } from '~/components/Flyout'
import { Skeleton } from '~/components/Skeleton'
import { Product } from '~/hooks/useProduct'
import { useNodeStaffAccess } from '~/hooks/useNodeStaffAccess'
import { useScope } from '~/hooks/useScope'
import { useSparkStaffAccess } from '~/hooks/useSparkBidder'
import { useCanCreateSparkForPubSeat } from '~/hooks/useSparkForPub'
import { useStep } from '~/utils/useStep'
import { useStepperFocus, validateStepOnEnter } from '~/utils/validateStepOnEnter'
import { countCompleteDeclaredPlacements, DeclaredPlacementRow, isDeclaredPlacementComplete } from './DeclaredPlacementsSection'
import { buildSeatName, defaultSeatMappingType, defaultSeatSetupMethod, SeatMappingType, SeatSetupMethod, seatSetupOptions, sparkDemandOption } from './seatSetupOption'
import { StepConfiguration } from './StepConfiguration'
import { StepReview } from './StepReview'
import { StepSetupMethod } from './StepSetupMethod'
import { useCreateBidderSeatSubmit } from './useCreateBidderSeatSubmit'

export type CreateSeatFormModel = {
  catalogBidderDisplayName: string
  /** Empty until a bidder is picked and a method is available for it. */
  setupMethod: SeatSetupMethod | ''
  name: string
  mappingType: SeatMappingType
  bidAdjustment: {
    enable: boolean
    adjustment: number
  }
  declaredPlacements: DeclaredPlacementRow[]
}

const STEP_COUNT = 3
const CONFIGURATION_STEP = 2
const TRACKING_PREFIX = 'pbs-create-seat-stepper'

const freshDeclaredPlacements = (bidderCode: string): DeclaredPlacementRow[] =>
  Array.from({ length: SPARK_FOR_PUB_MIN_DECLARED_PLACEMENTS }, (_, index) => ({ placementId: buildSparkForPubPlacementId(bidderCode, index + 1), bidParams: {} }))

type Props = {
  scopeId: string
  /**
   * `spark-demand` is the internal Spark flow: same three steps, but the bidder list is the adapters
   * Spark can carry demand for and the only setup method offered is the internal one.
   */
  variant?: 'catalog' | 'spark-demand'
  /** Absent when opened from the "New seat" button: the bidder is then picked on the first step. */
  preselectedBidderCatalog?: BidderCatalog
  onSeatCreated: (name: string, product: Product) => void
  onClose: () => void
}

export const CreateSeatStepper: FunctionComponent<Props> = (props) => {
  const isSparkDemand = props.variant === 'spark-demand'
  // Spark seats are a PBS type, so only that list can say whether one already exists.
  const { data: bidderSeats, isLoading: isLoadingSeats } = useBidderSeats('pbs')
  // Except the Spark Demand ones: they are internal, and `isInternal` is a filter the endpoint applies
  // in both directions, so the listing above answers "no seat" for every adapter that has one. The call
  // is Pubstack-only, hence gated on the variant that is itself only reachable from the internal tab.
  const { data: internalBidderSeats, isLoading: isLoadingInternalSeats } = useBidderSeats('pbs', { isInternal: true }, isSparkDemand)
  // Merged behind a memo because the form below keys its option-building on this array's identity. The
  // two queries filter on opposite values of `isInternal`, so no seat can appear in both; and merging
  // unconditionally is safe even when a warm cache serves the internal listing to the catalog variant,
  // since every predicate that variant asks also filters on a `type` no `spark_demand` seat carries.
  const allBidderSeats = useMemo(() => [...bidderSeats, ...internalBidderSeats], [bidderSeats, internalBidderSeats])
  // The ADM catalog is the superset (all bidders); the PBS one only lists server-side compatible
  // ones, which would hide every client-side-only integration. Spark Demand wants the opposite: only
  // the adapters flagged as compatible, which is the same set onboarding creates seats for.
  const { data: catalogBidders, isLoading: isLoadingCatalog } = useCatalog(isSparkDemand ? 'pbs' : 'adm', isSparkDemand ? { isPbstckBidderCompatible: true } : {}, !props.preselectedBidderCatalog)

  // useForm freezes its defaultValues at mount: don't mount the form before the seats and the catalog
  // are loaded, else an empty list wrongly starts the stepper on Spark despite an existing spark seat.
  if (isLoadingSeats || isLoadingCatalog || (isSparkDemand && isLoadingInternalSeats)) {
    return (
      <Flyout.Body>
        <Box pt={Sizes[8]} pb={Sizes[24]}>
          <Stack gap={Sizes[16]}>
            <Skeleton bigger width="100%" />
            <Skeleton bigger width="100%" />
            <Skeleton bigger width="60%" />
          </Stack>
        </Box>
      </Flyout.Body>
    )
  }

  // A bidder that forbids aliasing cannot get an extra seat at all, so it is no use offering it here.
  // The catalog grid does list it — that one shows the catalog whole, and says on the card what you can
  // do with each entry; this picker is only ever a list of things you can actually create. Spark Demand
  // is not aliasing: Pubstack creates the seat on its own behalf, so that rule does not apply to it.
  return (
    <CreateSeatStepperForm
      {...props}
      bidderSeats={allBidderSeats}
      catalogBidders={props.preselectedBidderCatalog ? undefined : isSparkDemand ? catalogBidders : catalogBidders.filter((bidder) => !bidder.disableAliasing)}
    />
  )
}

type FormProps = Props & {
  bidderSeats: BidderSeat[]
  catalogBidders?: BidderCatalog[]
}

const CreateSeatStepperForm: FunctionComponent<FormProps> = ({ scopeId, variant, preselectedBidderCatalog, onSeatCreated, onClose, bidderSeats, catalogBidders }) => {
  const scope = useScope()
  const user = useAuthenticatedUser()
  const canUseMarketplace = variant !== 'spark-demand' && user.isAdmin && user.hasFeature('marketplace')
  const { data: marketplaceBidders } = useMarketplaceBiddders(canUseMarketplace)
  const canCreateSparkForPubSeat = useCanCreateSparkForPubSeat()
  // Onboarded and visible to this viewer, so the flyout agrees with the catalog card.
  const { viewerOnboarded: isSparkOnboarded } = useSparkStaffAccess()
  const [currentStep, stepHelpers] = useStep(STEP_COUNT)
  const bodyRef = useStepperFocus(currentStep)
  const [selectedBidder, setSelectedBidder] = useState<BidderCatalog | undefined>(preselectedBidderCatalog)

  // The scope's entitlements, not the viewer's — the catalog card greys itself on exactly these, and a
  // card that says "subscribe" must not open a flyout that lets you create the seat anyway.
  const hasAdManagement = scope.hasFeature(UPGRADE_OFFER_FEATURE.one)
  // Staff included on a scope onboarded on Node and not yet opened to its users.
  const { hasNode } = useNodeStaffAccess()
  // On `sparkForNetwork` scopes the backend strips the customer's Spark seat from every listing a
  // non-Pubstack user can make, so `bidderSeats` cannot answer whether it exists there. The feature is
  // only ever flipped on scopes the Spark team has provisioned, so assume the seat does exist rather
  // than offer to create a second one and let the server backstop reject it.
  const isSparkSeatHiddenFromListings = scope.hasFeature('sparkForNetwork')

  const buildOptions = useCallback(
    (bidder: BidderCatalog) =>
      // Spark Demand offers exactly one thing, and it is not in the catalog's list: the seat is Pubstack's
      // own demand on someone else's adapter, so none of the entitlement rules apply — only "does it exist".
      variant === 'spark-demand'
        ? [sparkDemandOption(bidderSeats.some((seat) => seat.bidderCode === bidder.id && seat.type === 'spark_demand'))]
        : seatSetupOptions({
            bidder,
            hasAdManagement,
            hasNode,
            canCreateSparkForPubSeat,
            sparkForPubAlreadyExists: bidderSeats.some((seat) => seat.bidderCode === bidder.id && seat.type === 'spark_for_pubs'),
            isSparkOnboarded,
            // The onboarding already creates the scope's one Spark seat, so this is normally true — offering
            // a second would only produce a `pbstck-bidder-s2s` next to the `Spark` one.
            pbstckBidderSeatAlreadyExists: isSparkSeatHiddenFromListings || bidderSeats.some((seat) => seat.bidderCode === PBSTCK_BIDDER_SEAT_CODE && seat.type === 'node'),
            canCreateMarketplaceSeat: canUseMarketplace && marketplaceBidders.some(({ id, activated }) => id === bidder.id && activated),
          }),
    [variant, hasAdManagement, hasNode, canCreateSparkForPubSeat, bidderSeats, isSparkOnboarded, isSparkSeatHiddenFromListings, canUseMarketplace, marketplaceBidders]
  )

  const options = useMemo(() => (selectedBidder ? buildOptions(selectedBidder) : []), [selectedBidder, buildOptions])

  // Spark seats are always server-side: only expose the server-compatible bid params (same filter as BidderMapping).
  const bidParamSpecs = useMemo(() => (selectedBidder?.bidParams ?? []).filter((e) => !e.platform || e.platform.includes('server')), [selectedBidder])

  const initialMethod = (preselectedBidderCatalog ? defaultSeatSetupMethod(buildOptions(preselectedBidderCatalog)) : undefined) ?? ''

  const formMethods = useForm<CreateSeatFormModel>({
    defaultValues: {
      catalogBidderDisplayName: preselectedBidderCatalog?.displayName ?? '',
      setupMethod: initialMethod,
      name: preselectedBidderCatalog && initialMethod ? buildSeatName(initialMethod, preselectedBidderCatalog.id) : '',
      mappingType: initialMethod ? defaultSeatMappingType(initialMethod) : 'ad-unit',
      bidAdjustment: { enable: false, adjustment: 0 },
      declaredPlacements: preselectedBidderCatalog ? freshDeclaredPlacements(preselectedBidderCatalog.id) : [],
    },
    mode: 'onChange',
  })

  const {
    control,
    handleSubmit,
    formState: { isValid, isValidating },
    getValues,
    setValue,
    trigger,
  } = formMethods

  const { submit, isPending, nameError, clearNameError } = useCreateBidderSeatSubmit({
    scopeId,
    onSeatCreated,
    onClose,
    onNameError: () => stepHelpers.setStep(CONFIGURATION_STEP),
  })

  // Watch so the Next button re-enables as soon as enough placements are declared (declaredPlacements is set via setValue).
  const declaredPlacements = useWatch({ control, name: 'declaredPlacements' })
  const setupMethod = useWatch({ control, name: 'setupMethod' })
  const hasEnoughDeclaredPlacements = setupMethod !== 'spark-for-pubs' || countCompleteDeclaredPlacements(declaredPlacements, bidParamSpecs) >= SPARK_FOR_PUB_MIN_DECLARED_PLACEMENTS

  const handleBidderChange = (bidder: BidderCatalog) => {
    setSelectedBidder(bidder)
    const nextMethod = defaultSeatSetupMethod(buildOptions(bidder)) ?? ''
    setValue('setupMethod', nextMethod, { shouldValidate: true })
    setValue('name', nextMethod ? buildSeatName(nextMethod, bidder.id) : '', { shouldValidate: true })
    setValue('mappingType', nextMethod ? defaultSeatMappingType(nextMethod) : 'ad-unit')
    setValue('declaredPlacements', freshDeclaredPlacements(bidder.id))
    clearNameError()
  }

  const handleSetupMethodChange = (next: SeatSetupMethod) => {
    if (!selectedBidder) return
    setValue('setupMethod', next, { shouldValidate: true })
    setValue('name', buildSeatName(next, selectedBidder.id), { shouldValidate: true })
    // The offered mapping types differ per method (`device` is ADM-only), so never carry one over.
    setValue('mappingType', defaultSeatMappingType(next))
    clearNameError()
  }

  const goNext = async () => {
    if (currentStep === CONFIGURATION_STEP && !(await trigger(['name', 'bidAdjustment']))) return
    stepHelpers.goToNextStep()
  }

  const isNextDisabled = isValidating || (currentStep === 1 && !setupMethod) || (currentStep === CONFIGURATION_STEP && (!isValid || !hasEnoughDeclaredPlacements))
  const isCreateDisabled = !isValid || isPending

  const onSubmit = (value: CreateSeatFormModel) => {
    if (!selectedBidder || !value.setupMethod) return
    return submit(value.setupMethod, selectedBidder.id, {
      name: value.name,
      bidAdjustment: value.bidAdjustment,
      mappingType: value.mappingType,
      declaredPlacements: value.declaredPlacements.filter((row) => isDeclaredPlacementComplete(row, bidParamSpecs)),
    })
  }

  const selectedOption = options.find((option) => option.value === setupMethod)

  // Enter validates whichever step is open, exactly as its button does — including doing nothing when
  // that button is disabled.
  const handleEnter = validateStepOnEnter(() => {
    if (stepHelpers.canGoToNextStep) {
      if (!isNextDisabled) void goNext()
    } else if (!isCreateDisabled) {
      void handleSubmit(onSubmit)()
    }
  })

  return (
    <>
      <Flyout.Body ref={bodyRef} tabIndex={-1} className={'outline-none'} onKeyDown={handleEnter}>
        <Box pt={Sizes[8]} pb={Sizes[24]}>
          <MantineStepper active={currentStep - 1}>
            <MantineStepper.Step label="Type" completedIcon={1} />
            <MantineStepper.Step label="Configuration" completedIcon={2} />
            <MantineStepper.Step label="Review" completedIcon={3} />
          </MantineStepper>
        </Box>
        <FormProvider {...formMethods}>
          {currentStep === 1 && <StepSetupMethod options={options} catalogBidders={catalogBidders} onBidderChange={handleBidderChange} onSetupMethodChange={handleSetupMethodChange} />}
          {currentStep === CONFIGURATION_STEP && selectedBidder && (
            <StepConfiguration nameError={nameError} onNameChange={clearNameError} bidderDisplayName={selectedBidder.displayName} bidderCode={selectedBidder.id} bidParamSpecs={bidParamSpecs} />
          )}
          {currentStep === 3 && selectedBidder && (
            <StepReview values={getValues()} bidderDisplayName={selectedBidder.displayName} methodTitle={selectedOption?.title ?? ''} bidParamSpecs={bidParamSpecs} />
          )}
        </FormProvider>
      </Flyout.Body>
      <Flyout.Actions>
        {stepHelpers.canGoToPrevStep && (
          <Button className="mr-auto" variant={'tertiary'} onClick={stepHelpers.goToPrevStep} trackingLabel={`${TRACKING_PREFIX}-step-${currentStep}-previous`}>
            Previous
          </Button>
        )}
        {stepHelpers.canGoToNextStep ? (
          <Button variant={'primary'} disabled={isNextDisabled} onClick={goNext} trackingLabel={`${TRACKING_PREFIX}-step-${currentStep}-next`}>
            Next
          </Button>
        ) : (
          <Button
            variant={'primary'}
            disabled={isCreateDisabled}
            iconName={isPending ? 'pubstack' : undefined}
            iconSpin={isPending}
            onClick={handleSubmit(onSubmit)}
            trackingLabel={`${TRACKING_PREFIX}-save`}
          >
            {isPending ? 'Creating seat...' : 'Create seat'}
          </Button>
        )}
      </Flyout.Actions>
    </>
  )
}
